import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, FilterIcon, PlusIcon, DownloadIcon, MailIcon, EyeIcon, EditIcon, ChevronDownIcon, XIcon, CheckIcon, FileTextIcon, ClipboardListIcon } from 'lucide-react';
import { StatusBadge } from '../StatusBadge';
import { formatCurrency } from '../utils/currencyFormatter';
export function RCTIList({
  rctis = [],
  onView,
  onEdit,
  onCreate,
  locale = 'en-US'
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    vendor: '',
    entity: '',
    site: '',
    currency: '',
    taxCode: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState('rctiDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const actionMenuRef = useRef(null);
  // Sample data for filters
  const vendors = [{
    id: 'v1',
    name: 'ABC Construction'
  }, {
    id: 'v2',
    name: 'XYZ Services'
  }, {
    id: 'v3',
    name: 'Global Contractors'
  }, {
    id: 'v4',
    name: 'Metro Supplies'
  }];
  const entities = [{
    id: 'e1',
    name: 'Head Office'
  }, {
    id: 'e2',
    name: 'Regional Division'
  }, {
    id: 'e3',
    name: 'International Subsidiary'
  }];
  const sites = [{
    id: 's1',
    name: 'Main Campus'
  }, {
    id: 's2',
    name: 'Downtown Office'
  }, {
    id: 's3',
    name: 'Manufacturing Plant'
  }];
  const currencies = [{
    code: 'USD',
    name: 'US Dollar'
  }, {
    code: 'EUR',
    name: 'Euro'
  }, {
    code: 'GBP',
    name: 'British Pound'
  }, {
    code: 'AUD',
    name: 'Australian Dollar'
  }];
  const taxCodes = [{
    code: 'GST10',
    name: 'GST 10%'
  }, {
    code: 'VAT20',
    name: 'VAT 20%'
  }, {
    code: 'EXEMPT',
    name: 'Tax Exempt'
  }];
  const statuses = [{
    value: 'Draft',
    label: 'Draft'
  }, {
    value: 'Sent',
    label: 'Sent'
  }, {
    value: 'Approved',
    label: 'Approved'
  }];
  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (actionMenuOpen !== null && !actionMenuRef.current?.contains(event.target) && !event.target.closest('button[data-rcti-id]')) {
        setActionMenuOpen(null);
      }
    };
    if (actionMenuOpen !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [actionMenuOpen]);
  const handleFilterChange = (name, value) => {
    setFilters({
      ...filters,
      [name]: value
    });
    setCurrentPage(1);
  };
  const clearFilters = () => {
    setFilters({
      vendor: '',
      entity: '',
      site: '',
      currency: '',
      taxCode: '',
      status: '',
      dateFrom: '',
      dateTo: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };
  const toggleSort = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const toggleActionMenu = id => {
    setActionMenuOpen(actionMenuOpen === id ? null : id);
  };
  const handleDownloadPDF = rcti => {
    console.log(`Downloading PDF for RCTI: ${rcti.rctiNumber}`);
    setActionMenuOpen(null);
  };
  const handleSendEmail = rcti => {
    console.log(`Sending email for RCTI: ${rcti.rctiNumber}`);
    setActionMenuOpen(null);
  };
  const handleExport = rcti => {
    console.log(`Exporting RCTI: ${rcti.rctiNumber}`);
    setActionMenuOpen(null);
  };
  // Filter and sort RCTIs
  const filteredRCTIs = rctis.filter(rcti => {
    // Search term filtering
    if (searchTerm && !rcti.rctiNumber.toLowerCase().includes(searchTerm.toLowerCase()) && !rcti.vendor.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    // Specific filters
    if (filters.vendor && rcti.vendor.id !== filters.vendor) return false;
    if (filters.entity && rcti.entity.id !== filters.entity) return false;
    if (filters.site && rcti.site.id !== filters.site) return false;
    if (filters.currency && rcti.currency !== filters.currency) return false;
    if (filters.taxCode && !rcti.taxCodes.includes(filters.taxCode)) return false;
    if (filters.status && rcti.status !== filters.status) return false;
    // Date range filtering
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      const rctiDate = new Date(rcti.rctiDate);
      if (rctiDate < fromDate) return false;
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      const rctiDate = new Date(rcti.rctiDate);
      if (rctiDate > toDate) return false;
    }
    return true;
  }).sort((a, b) => {
    // Sort logic
    if (sortField === 'rctiNumber') {
      return sortDirection === 'asc' ? a.rctiNumber.localeCompare(b.rctiNumber) : b.rctiNumber.localeCompare(a.rctiNumber);
    } else if (sortField === 'vendor') {
      return sortDirection === 'asc' ? a.vendor.name.localeCompare(b.vendor.name) : b.vendor.name.localeCompare(a.vendor.name);
    } else if (sortField === 'rctiDate') {
      return sortDirection === 'asc' ? new Date(a.rctiDate) - new Date(b.rctiDate) : new Date(b.rctiDate) - new Date(a.rctiDate);
    } else if (sortField === 'grandTotal') {
      return sortDirection === 'asc' ? a.grandTotal - b.grandTotal : b.grandTotal - a.grandTotal;
    }
    return 0;
  });
  // Pagination
  const totalRCTIs = filteredRCTIs.length;
  const totalPages = Math.ceil(totalRCTIs / pageSize);
  const paginatedRCTIs = filteredRCTIs.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const getStatusColor = status => {
    switch (status) {
      case 'Draft':
        return 'warning';
      case 'Sent':
        return 'info';
      case 'Approved':
        return 'success';
      default:
        return 'default';
    }
  };
  return <div className="space-y-6" data-id="element-1345">
      {/* Header with title and create button */}
      <div className="flex justify-between items-center" data-id="element-1346">
        <div data-id="element-1347">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-1348">
            Recipient Created Tax Invoices
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-1349">
            Manage RCTIs created on behalf of vendors
          </p>
        </div>
        <button onClick={onCreate} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1350">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-1351" />
          Create RCTI
        </button>
      </div>

      {/* Search and filters */}
      <div className="bg-white shadow-sm rounded-lg p-4" data-id="element-1352">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-1353">
          <div className="flex-1 max-w-md" data-id="element-1354">
            <div className="relative rounded-md shadow-sm" data-id="element-1355">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1356">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1357" />
              </div>
              <input type="text" value={searchTerm} onChange={e => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search RCTI number or vendor..." data-id="element-1358" />
            </div>
          </div>
          <div className="flex space-x-2" data-id="element-1359">
            <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1360">
              <FilterIcon className="h-4 w-4 mr-2" data-id="element-1361" />
              Filters
              {Object.values(filters).some(f => f !== '') && <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-1362">
                  {Object.values(filters).filter(f => f !== '').length}
                </span>}
            </button>
            <button onClick={() => console.log('Export all filtered RCTIs')} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1363">
              <DownloadIcon className="h-4 w-4 mr-2" data-id="element-1364" />
              Export
            </button>
          </div>
        </div>

        {/* Filter chips */}
        {Object.values(filters).some(f => f !== '') && <div className="mt-4 flex flex-wrap gap-2" data-id="element-1365">
            {filters.vendor && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800" data-id="element-1366">
                Vendor: {vendors.find(v => v.id === filters.vendor)?.name}
                <button type="button" onClick={() => handleFilterChange('vendor', '')} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none" data-id="element-1367">
                  <XIcon className="h-3 w-3" data-id="element-1368" />
                </button>
              </span>}
            {filters.entity && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800" data-id="element-1369">
                Entity: {entities.find(e => e.id === filters.entity)?.name}
                <button type="button" onClick={() => handleFilterChange('entity', '')} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-purple-400 hover:bg-purple-200 hover:text-purple-500 focus:outline-none" data-id="element-1370">
                  <XIcon className="h-3 w-3" data-id="element-1371" />
                </button>
              </span>}
            {filters.site && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-1372">
                Site: {sites.find(s => s.id === filters.site)?.name}
                <button type="button" onClick={() => handleFilterChange('site', '')} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-500 focus:outline-none" data-id="element-1373">
                  <XIcon className="h-3 w-3" data-id="element-1374" />
                </button>
              </span>}
            {filters.currency && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800" data-id="element-1375">
                Currency: {filters.currency}
                <button type="button" onClick={() => handleFilterChange('currency', '')} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-yellow-400 hover:bg-yellow-200 hover:text-yellow-500 focus:outline-none" data-id="element-1376">
                  <XIcon className="h-3 w-3" data-id="element-1377" />
                </button>
              </span>}
            {filters.taxCode && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-red-100 text-red-800" data-id="element-1378">
                Tax Code: {filters.taxCode}
                <button type="button" onClick={() => handleFilterChange('taxCode', '')} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-red-400 hover:bg-red-200 hover:text-red-500 focus:outline-none" data-id="element-1379">
                  <XIcon className="h-3 w-3" data-id="element-1380" />
                </button>
              </span>}
            {filters.status && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-gray-100 text-gray-800" data-id="element-1381">
                Status: {filters.status}
                <button type="button" onClick={() => handleFilterChange('status', '')} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-500 focus:outline-none" data-id="element-1382">
                  <XIcon className="h-3 w-3" data-id="element-1383" />
                </button>
              </span>}
            {(filters.dateFrom || filters.dateTo) && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800" data-id="element-1384">
                Date: {filters.dateFrom ? filters.dateFrom : 'Any'} to{' '}
                {filters.dateTo ? filters.dateTo : 'Any'}
                <button type="button" onClick={() => {
            handleFilterChange('dateFrom', '');
            handleFilterChange('dateTo', '');
          }} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-indigo-400 hover:bg-indigo-200 hover:text-indigo-500 focus:outline-none" data-id="element-1385">
                  <XIcon className="h-3 w-3" data-id="element-1386" />
                </button>
              </span>}
            <button type="button" onClick={clearFilters} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1387">
              Clear All
            </button>
          </div>}

        {/* Expanded filters */}
        {showFilters && <div className="mt-4 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6" data-id="element-1388">
            <div className="sm:col-span-2" data-id="element-1389">
              <label htmlFor="vendor-filter" className="block text-sm font-medium text-gray-700" data-id="element-1390">
                Vendor
              </label>
              <select id="vendor-filter" value={filters.vendor} onChange={e => handleFilterChange('vendor', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-1391">
                <option value="" data-id="element-1392">All Vendors</option>
                {vendors.map(vendor => <option key={vendor.id} value={vendor.id} data-id="element-1393">
                    {vendor.name}
                  </option>)}
              </select>
            </div>
            <div className="sm:col-span-2" data-id="element-1394">
              <label htmlFor="entity-filter" className="block text-sm font-medium text-gray-700" data-id="element-1395">
                Entity
              </label>
              <select id="entity-filter" value={filters.entity} onChange={e => handleFilterChange('entity', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-1396">
                <option value="" data-id="element-1397">All Entities</option>
                {entities.map(entity => <option key={entity.id} value={entity.id} data-id="element-1398">
                    {entity.name}
                  </option>)}
              </select>
            </div>
            <div className="sm:col-span-2" data-id="element-1399">
              <label htmlFor="site-filter" className="block text-sm font-medium text-gray-700" data-id="element-1400">
                Site
              </label>
              <select id="site-filter" value={filters.site} onChange={e => handleFilterChange('site', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-1401">
                <option value="" data-id="element-1402">All Sites</option>
                {sites.map(site => <option key={site.id} value={site.id} data-id="element-1403">
                    {site.name}
                  </option>)}
              </select>
            </div>
            <div className="sm:col-span-2" data-id="element-1404">
              <label htmlFor="currency-filter" className="block text-sm font-medium text-gray-700" data-id="element-1405">
                Currency
              </label>
              <select id="currency-filter" value={filters.currency} onChange={e => handleFilterChange('currency', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-1406">
                <option value="" data-id="element-1407">All Currencies</option>
                {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-1408">
                    {currency.code} - {currency.name}
                  </option>)}
              </select>
            </div>
            <div className="sm:col-span-2" data-id="element-1409">
              <label htmlFor="tax-code-filter" className="block text-sm font-medium text-gray-700" data-id="element-1410">
                Tax Code
              </label>
              <select id="tax-code-filter" value={filters.taxCode} onChange={e => handleFilterChange('taxCode', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-1411">
                <option value="" data-id="element-1412">All Tax Codes</option>
                {taxCodes.map(taxCode => <option key={taxCode.code} value={taxCode.code} data-id="element-1413">
                    {taxCode.code} - {taxCode.name}
                  </option>)}
              </select>
            </div>
            <div className="sm:col-span-2" data-id="element-1414">
              <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700" data-id="element-1415">
                Status
              </label>
              <select id="status-filter" value={filters.status} onChange={e => handleFilterChange('status', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-1416">
                <option value="" data-id="element-1417">All Statuses</option>
                {statuses.map(status => <option key={status.value} value={status.value} data-id="element-1418">
                    {status.label}
                  </option>)}
              </select>
            </div>
            <div className="sm:col-span-2" data-id="element-1419">
              <label htmlFor="date-from-filter" className="block text-sm font-medium text-gray-700" data-id="element-1420">
                Date From
              </label>
              <input type="date" id="date-from-filter" value={filters.dateFrom} onChange={e => handleFilterChange('dateFrom', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-1421" />
            </div>
            <div className="sm:col-span-2" data-id="element-1422">
              <label htmlFor="date-to-filter" className="block text-sm font-medium text-gray-700" data-id="element-1423">
                Date To
              </label>
              <input type="date" id="date-to-filter" value={filters.dateTo} onChange={e => handleFilterChange('dateTo', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-1424" />
            </div>
          </div>}
      </div>

      {/* RCTIs table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-1425">
        {paginatedRCTIs.length === 0 ? <div className="px-4 py-8 text-center text-gray-500" data-id="element-1426">
            <FileTextIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-1427" />
            <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-1428">
              No RCTIs found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-1429">
              {searchTerm || Object.values(filters).some(f => f !== '') ? 'Try adjusting your search or filter criteria.' : 'Get started by creating a new RCTI.'}
            </p>
            {!(searchTerm || Object.values(filters).some(f => f !== '')) && <div className="mt-6" data-id="element-1430">
                <button onClick={onCreate} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-1431">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-1432" />
                  Create RCTI
                </button>
              </div>}
          </div> : <div className="overflow-x-auto" data-id="element-1433">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1434">
              <thead className="bg-gray-50" data-id="element-1435">
                <tr data-id="element-1436">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('rctiNumber')} data-id="element-1437">
                    <div className="flex items-center" data-id="element-1438">
                      <span data-id="element-1439">RCTI Number</span>
                      {sortField === 'rctiNumber' && <span className="ml-1" data-id="element-1440">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('vendor')} data-id="element-1441">
                    <div className="flex items-center" data-id="element-1442">
                      <span data-id="element-1443">Vendor</span>
                      {sortField === 'vendor' && <span className="ml-1" data-id="element-1444">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1445">
                    Entity / Site
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('rctiDate')} data-id="element-1446">
                    <div className="flex items-center" data-id="element-1447">
                      <span data-id="element-1448">RCTI Date</span>
                      {sortField === 'rctiDate' && <span className="ml-1" data-id="element-1449">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1450">
                    Tax Identifier
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1451">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('grandTotal')} data-id="element-1452">
                    <div className="flex items-center" data-id="element-1453">
                      <span data-id="element-1454">Grand Total</span>
                      {sortField === 'grandTotal' && <span className="ml-1" data-id="element-1455">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1456">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1457">
                {paginatedRCTIs.map(rcti => <tr key={rcti.id} className="hover:bg-gray-50" data-id="element-1458">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-1459">
                      {rcti.rctiNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1460">
                      <div className="font-medium" data-id="element-1461">{rcti.vendor.name}</div>
                      {rcti.reference && <div className="text-xs text-gray-400" data-id="element-1462">
                          Ref: {rcti.reference}
                        </div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1463">
                      {rcti.entity.name}
                      {rcti.site && <>
                          <span className="mx-1 text-gray-300" data-id="element-1464">/</span>
                          {rcti.site.name}
                        </>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1465">
                      {new Date(rcti.rctiDate).toLocaleDateString(locale)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1466">
                      {rcti.taxIdentifier}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1467">
                      <StatusBadge status={getStatusColor(rcti.status)} text={rcti.status} data-id="element-1468" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1469">
                      {formatCurrency(rcti.grandTotal, rcti.currency, locale)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1470">
                      <div className="flex justify-end" data-id="element-1471">
                        <button onClick={() => onView(rcti)} className="text-indigo-600 hover:text-indigo-900 mr-3" title="View" data-id="element-1472">
                          <EyeIcon className="h-5 w-5" data-id="element-1473" />
                        </button>
                        {rcti.status === 'Draft' && <button onClick={() => onEdit(rcti)} className="text-blue-600 hover:text-blue-900 mr-3" title="Edit" data-id="element-1474">
                            <EditIcon className="h-5 w-5" data-id="element-1475" />
                          </button>}
                        <div className="relative" data-id="element-1476">
                          <button data-rcti-id={rcti.id} onClick={() => toggleActionMenu(rcti.id)} className="text-gray-400 hover:text-gray-500" title="More actions" data-id="element-1477">
                            <ChevronDownIcon className="h-5 w-5" data-id="element-1478" />
                          </button>
                          {actionMenuOpen === rcti.id && <div ref={actionMenuRef} className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" data-id="element-1479">
                              <div className="py-1" role="menu" aria-orientation="vertical" data-id="element-1480">
                                <button onClick={() => handleDownloadPDF(rcti)} className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem" data-id="element-1481">
                                  <DownloadIcon className="mr-3 h-5 w-5 text-gray-400" data-id="element-1482" />
                                  Download PDF
                                </button>
                                <button onClick={() => handleSendEmail(rcti)} className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem" data-id="element-1483">
                                  <MailIcon className="mr-3 h-5 w-5 text-gray-400" data-id="element-1484" />
                                  Email to Vendor
                                </button>
                                <button onClick={() => handleExport(rcti)} className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem" data-id="element-1485">
                                  <ClipboardListIcon className="mr-3 h-5 w-5 text-gray-400" data-id="element-1486" />
                                  Export
                                </button>
                              </div>
                            </div>}
                        </div>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Pagination */}
      {totalRCTIs > 0 && <div className="flex items-center justify-between" data-id="element-1487">
          <div className="flex-1 flex justify-between sm:hidden" data-id="element-1488">
            <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-1489">
              Previous
            </button>
            <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-1490">
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-1491">
            <div data-id="element-1492">
              <p className="text-sm text-gray-700" data-id="element-1493">
                Showing{' '}
                <span className="font-medium" data-id="element-1494">
                  {(currentPage - 1) * pageSize + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium" data-id="element-1495">
                  {Math.min(currentPage * pageSize, totalRCTIs)}
                </span>{' '}
                of <span className="font-medium" data-id="element-1496">{totalRCTIs}</span> results
              </p>
            </div>
            <div data-id="element-1497">
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-1498">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-1499">
                  <span className="sr-only" data-id="element-1500">First</span>
                  <div className="h-5 w-5 flex items-center justify-center" data-id="element-1501">
                    <span className="text-xs" data-id="element-1502">«</span>
                  </div>
                </button>
                <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-1503">
                  <span className="sr-only" data-id="element-1504">Previous</span>
                  <ChevronDownIcon className="h-5 w-5 rotate-90" data-id="element-1505" />
                </button>
                {/* Page numbers */}
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return <button key={pageNum} onClick={() => setCurrentPage(pageNum)} className={`relative inline-flex items-center px-4 py-2 border ${currentPage === pageNum ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'} text-sm font-medium`} data-id="element-1506">
                      {pageNum}
                    </button>;
            })}
                <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-1507">
                  <span className="sr-only" data-id="element-1508">Next</span>
                  <ChevronDownIcon className="h-5 w-5 -rotate-90" data-id="element-1509" />
                </button>
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-1510">
                  <span className="sr-only" data-id="element-1511">Last</span>
                  <div className="h-5 w-5 flex items-center justify-center" data-id="element-1512">
                    <span className="text-xs" data-id="element-1513">»</span>
                  </div>
                </button>
              </nav>
            </div>
          </div>
        </div>}
    </div>;
}